import React, { useState, useEffect } from 'react';
import FolderDrawerTreeView from '../FolderDrawerTreeView';
import TableComponent from '../TableComponent';
import { fetchModulesTreeViewData, fetchModulesWithCount } from '../../utils/api';
import { getAvailableTestCasesForGroup, associateTestCasesWithTestGroup } from '../../utils/api/testgroup_apis';
import { STATUS } from '../../utils/api/sharedfunction';
import { useSelector } from 'react-redux';
import { Box, Typography, TextField, CircularProgress, Tooltip } from '@mui/material';
import { Folder as FolderIcon } from '@mui/icons-material';
import LabelChipContainer from '../chips/LabelChipContainer';
import { MissingData } from '../../enums/enumRegistry';
import { useSnackbar } from '../../utils/useSnackbar';
import { CommonFooter, InputBox } from '../FormComponent';
import ModalWrapper from '../ModalWrapper';
import styles from '../../styles/testGroupDetail/index.module.scss';
import { getAutomationStatusVariant } from '../chips/ChipComponent/chipVariants';
import ChipComponent from '../chips/ChipComponent/ChipComponent';

const MAX_TEST_CASES_PER_GROUP = 100;

const AddTestCasesToGroupModal = ({ open, onClose, testGroup, onSuccess, currentTestCaseCount = 0 }) => {
  const { showSnackbar } = useSnackbar();
  const [step, setStep] = useState(1);
  const [modules, setModules] = useState([]);
  const [selectedModuleId, setSelectedModuleId] = useState(null);
  const [selected, setSelected] = useState([]);
  const [testCases, setTestCases] = useState([]);
  const [selectedTestCaseIds, setSelectedTestCaseIds] = useState([]);
  const [loadingModules, setLoadingModules] = useState(false);
  const [loadingTestCases, setLoadingTestCases] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [testCaseSearch, setTestCaseSearch] = useState('');
  const [testCaseTotal, setTestCaseTotal] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const selectedProject = useSelector(state => state.projects.selectedProject);
  const [filterModel, setFilterModel] = useState({ 
    items : [],
    quickFilterValues: [],
  });
  const [sortModel, setSortModel] = useState([
    { field: 'created_at', sort: 'desc' }
  ]);
  const [adding, setAdding] = useState(false);
  // Fetch modules on open
  useEffect(() => {
    if (open) {
      setStep(1);
      setSelectedModuleId(null);
      setSelected([]);
      setTestCases([]);
      setSelectedTestCaseIds([]);
      setSearchTerm('');
      setTestCaseSearch('');
      setPaginationModel({ page: 0, pageSize: 25 });
      setLoadingModules(true);
      fetchModulesWithCount(testGroup?.id ? { excludeTestGroupId: testGroup.id } : undefined).then(data => {
        setModules(data || []);
        setLoadingModules(false);
      });
    }
  }, [open]);

  // Compose modules with 'All Test Cases' node at the top
  // const allNode = { id: 'default', name: 'All Test Cases', individualcount: 0, totalcount: 0, children: [] };
  const modulesWithAll = [...modules];

  // Fetch test cases when module is selected or search/page changes
  useEffect(() => {
    if (step === 2 && selectedModuleId && testGroup?.id) {
      setLoadingTestCases(true);

      // Build the payload object for the new API
      const payload = {
        testGroupId: testGroup.id,
        moduleId: selectedModuleId !== 'default' ? selectedModuleId : undefined,
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
      };

      try {
        if (testCaseSearch?.trim()?.length > 0) {
          payload.search = testCaseSearch;
        }
      } catch (error) {
        console.error('Error setting search term', error);
      }

      if (sortModel.length > 0) {
        payload.sortField = sortModel[0].field === 'key' ? 'key_counter' : sortModel[0].field;
        payload.sortOrder = sortModel[0].sort;
      }

      getAvailableTestCasesForGroup(payload).then(response => {
        if (response.status === STATUS.SUCCESS) {
          setTestCases(response.data?.testCases || []);
          setTestCaseTotal(response.data?.pagination?.total || 0);
        } else {
          setTestCases([]);
          setTestCaseTotal(0);
        }
        setLoadingTestCases(false);
      });
    }
  }, [step, selectedModuleId, testCaseSearch, paginationModel, selectedProject?.id, sortModel, filterModel, testGroup?.id, testCaseSearch]);

  const handleModuleSelect = (moduleId) => {
    setSelectedModuleId(moduleId);
    setStep(2);
    setPaginationModel({ page: 0, pageSize: 25 });
    setTestCaseSearch('');
    setSelectedTestCaseIds([]);
    setFilterModel({ items: [], quickFilterValues: [] });
    setSortModel([{ field: 'created_at', sort: 'desc' }]);
  };

  const handleBack = () => {
    setStep(1);
    setSelectedModuleId(null);
    setSelectedTestCaseIds([]);
    setFilterModel({ items: [], quickFilterValues: [] });
    setSortModel([{ field: 'created_at', sort: 'desc' }]);
  };

  const remainingCapacity = MAX_TEST_CASES_PER_GROUP - currentTestCaseCount;
  const isOverLimit = selectedTestCaseIds.length > remainingCapacity;

  const handleAdd = async () => {
    if (!testGroup?.id || selectedTestCaseIds.length === 0) return;
    if (isOverLimit) {
      showSnackbar(`Cannot add ${selectedTestCaseIds.length} test case(s). This group already has ${currentTestCaseCount} test case(s) and the maximum is ${MAX_TEST_CASES_PER_GROUP}. You can add up to ${remainingCapacity} more.`, 'error');
      return;
    }
    try {
      setAdding(true);
      const response = await associateTestCasesWithTestGroup({
        testGroupId: testGroup.id,
        testCaseIds: selectedTestCaseIds,
      });
      
      if (response.status === STATUS.SUCCESS) {
        if (onSuccess) onSuccess(false);
        const tempTestCases = [...testCases];
        const filteredTestCases = tempTestCases.filter(testCase => !selectedTestCaseIds.includes(testCase.id));
        setTestCases(filteredTestCases);
        showSnackbar(response.message || 'Test cases added to group successfully', 'success');
      } else {
        showSnackbar(response.error || 'Failed to add test cases to group', 'error');
      }
    } catch (error) {
      showSnackbar(error?.message || 'Failed to add test cases to group', 'error');
    } finally {
      setAdding(false);
    }
  };

  // Table columns for test cases (minimal)
  const columns = [
    { field: '__check__', hideable: false, sortable: false, filterable: false, minWidth: 55, maxWidth: 55, resizable: false, headerAlign: 'center', align: 'center', hideable: false },
    { field: 'key', headerName: 'Test Case ID', minWidth: 120, maxWidth: 140, flex: 1 },
    { field: 'title', headerName: 'Summary', minWidth: 250, flex: 2 },
    { field: 'type', headerName: 'Type', minWidth: 100, maxWidth: 120, flex: 1 },
    { field: 'priority', headerName: 'Priority', minWidth: 100, maxWidth: 120, flex: 1 },
    { field: 'status', headerName: 'Status', minWidth: 100, maxWidth: 120, flex: 1 },
    { field: 'automation_status', headerName: 'Automation Status', minWidth: 170, maxWidth: 180, flex: 1, headerAlign: 'left', renderCell: (params) => (
      <ChipComponent variant={getAutomationStatusVariant(params?.row?.automation_status)} size="small">
        {params?.row?.automation_status}
      </ChipComponent>
    ) },
    { field : 'labels', headerName: 'Labels', minWidth: 220, flex: 1, renderCell: (params) => {
      const labels = params.row.labels || [];
      if (!labels || labels.length === 0) {
        return (
          <Tooltip title={MissingData.NO_DATA}>
            <span className="truncate">{MissingData.NONE}</span>
          </Tooltip>
        );
      }

      const maxWidth = params.colDef.computedWidth - 15;
      return (
        <LabelChipContainer labels={labels} columnWidth={maxWidth} />
      );
    }},
  ];

  // Modal content for each step
  let content;
  if (step === 1) {
    content = (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {loadingModules ? <CircularProgress /> : (
          <Box sx={{ flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column', justifyContent: 'stretch' }}>
            <FolderDrawerTreeView
              data={modulesWithAll}
              title={selectedProject?.name || 'Project'}
              onLeafNodeClick={handleModuleSelect}
              hideHeader={false}
              showSearch={true}
              smallView={true}
              selected={selected}
              setSelected={setSelected}
              renderLabel={(node) => node.id === 'default' ? (
                <Box sx={{ display: 'flex', alignItems: 'center', fontWeight: 700, color: '#2563eb' }}>
                  <FolderIcon sx={{ mr: 1, color: '#2563eb' }} />
                  {node.name}
                </Box>
              ) : undefined}
            />
          </Box>
        )}
      </Box>
    );
  } else if (step === 2) {
    content = (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Select test cases to add to <b>{testGroup?.name}</b>:</Typography>
        <Typography variant="body2" sx={{ mb: 1, color: remainingCapacity <= 0 ? 'error.main' : 'text.secondary' }}>
          {currentTestCaseCount} / {MAX_TEST_CASES_PER_GROUP} test cases in group{remainingCapacity > 0 ? ` — you can add up to ${remainingCapacity} more` : ' — group is at capacity'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <InputBox 
            label="Search Test Cases"
            value={testCaseSearch}
            onChange={e => {
              setTestCaseSearch(e.target.value);
              setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
              setFilterModel(model => ({ ...model, quickFilterValues: e.target.value ? [e.target.value] : [] }));
            }}
            variant="outlined"
            placeholder="Search Test Cases"
          />
        </Box>
        <Box sx={{ flex: '1 1 50vh', minHeight: 0, height: '100%' }}>
          {/* <TableComponent
            viewType='testCase'
            rows={testCases}
            columns={columns}
            isCheckbox={true}
            isRowSelection={true}
            selectedRows={selectedTestCaseIds}
            handleRowSelection={setSelectedTestCaseIds}
            loading={loadingTestCases}
            idField="id"
            isPagination={true}
            rowCount={testCaseTotal}
            paginationMode='server'
            paginationModel={paginationModel}
            handleCustomPaginationPageChange={(event, page) => setPaginationModel({ ...paginationModel, page: page })}
            handleCustomPaginationPageSizeChange={(pageSize) => setPaginationModel({ ...paginationModel, page: 0, pageSize: pageSize })}
            isCustomPagination={true}
            filterModel={filterModel}
            handleFilterModelChange={setFilterModel}
            filterMode='server'
            sortingMode='server'
            handleSortModelChange={setSortModel}
            sortModel={sortModel}
            getRowHeight={() => 'auto'}
            getEstimatedRowHeight={() => 60}
            tableHeight="100%"
          /> */}

<TableComponent
        viewType="testCase"
        title={"Test Cases"}
        headerActionButtons={[]}
        rows={testCases}
        columns={columns}
        isCheckbox={true}
        loading={loadingTestCases}
        idField="id"
        isColumnResizable={!loadingTestCases}
        isPagination={true}
        isRowSelection={true}
        handleRowSelection={(selectionModel)=> setSelectedTestCaseIds(selectionModel)}
        selectedRows={selectedTestCaseIds}
        paginationMode="server"
        rowCount={testCaseTotal}
        paginationModel={paginationModel}
        filterMode="server"
        handleFilterModelChange={(model) => {
          setFilterModel(model);
        }}
        sortingMode="server"
        sortModel={sortModel}
        handleSortModelChange={(model) => {
          setSortModel(model);
          setPaginationModel((prev) => ({ ...prev, page: 0 }));
        }}
        handleCustomPaginationPageChange={(event, page)=> setPaginationModel({...paginationModel, page: page})}
        handleCustomPaginationPageSizeChange={(pageSize)=> setPaginationModel({page: 0, pageSize: pageSize})}
        isCustomPagination={true}
        getRowHeight={() => 'auto'}
        getEstimatedRowHeight={() => 100}
        customStyles={{
          '& .MuiDataGrid-cell': {
            py: 1,
            display: 'flex',
            alignItems: 'center',
            paddingLeft: '18px',
            paddingRight: '18px',
            //height: '70px',
          },
        }}
        hideColumns={['title']}
      />
        </Box>
      </Box>
    );
  }

  // Modal buttons
  const buttons = [
    { label: 'Close', variant: 'outline', onClick: onClose, className: 'primary', disabled: adding },
    ...(step === 2 ? [
      { label: 'Back', variant: 'outline', onClick: handleBack, className: 'primary', disabled: adding },
      { label: isOverLimit ? `Limit: ${remainingCapacity} remaining` : 'Add', variant: 'primary',  className: 'white bg-blue600', onClick: handleAdd, disabled: selectedTestCaseIds.length === 0 || adding || isOverLimit || remainingCapacity <= 0, loading: adding },
    ] : []),
  ];

  return (
    <ModalWrapper
      isOpen={open}
      onClose={onClose}
      title="Add Test Cases to Group"
      size="large"
      footer={
        <CommonFooter
          buttons={buttons}
          align="right"
        />
      }
      children={<div className={styles.m_tb_10}>{content}</div>}
    />
  );
};

export default AddTestCasesToGroupModal; 